import React from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Box,
  IconButton,
  Divider,
  Chip,
} from "@mui/material";
import { Add, Remove, ShoppingCart } from "@mui/icons-material";
import { useIntl, FormattedMessage } from "react-intl";
import { Price } from "../../redux/types/types";

interface CartItem {
  priceId: number;
  price: Price;
  quantity: number;
}

interface PackageSelectorProps {
  packages: Price[];
  cartItems: CartItem[];
  onAddToCart: (price: Price, quantity: number) => void;
  getCartItemQuantity: (priceId: number) => number;
  calculateTotal: () => number;
  formatPrice: (amount: number) => string;
}

export const PackageSelector: React.FC<PackageSelectorProps> = ({
  packages,
  cartItems,
  onAddToCart,
  getCartItemQuantity,
  calculateTotal,
  formatPrice,
}) => {
  const intl = useIntl();

  const handleQuantityChange = (price: Price, newQuantity: number) => {
    const quantity = Math.max(0, newQuantity);
    onAddToCart(price, quantity);
  };

  const incrementQuantity = (price: Price) => {
    const currentQuantity = getCartItemQuantity(price.id);
    handleQuantityChange(price, currentQuantity + 1);
  };

  const decrementQuantity = (price: Price) => {
    const currentQuantity = getCartItemQuantity(price.id);
    handleQuantityChange(price, currentQuantity - 1);
  };

  return (
    <Box>
      <Typography
        variant="h6"
        gutterBottom
        sx={{ display: "flex", alignItems: "center", gap: 1 }}
      >
        <ShoppingCart />
        <FormattedMessage id="payment.selectPackages" />
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {packages.map((price) => {
          const quantity = getCartItemQuantity(price.id);
          const isSelected = quantity > 0;

          return (
            <Grid item xs={12} sm={6} md={4} key={price.id}>
              <Card
                sx={{
                  border: isSelected ? "2px solid" : "1px solid",
                  borderColor: isSelected ? "primary.main" : "divider",
                  backgroundColor: isSelected
                    ? "action.selected"
                    : "background.paper",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    boxShadow: 2,
                  },
                }}
              >
                <CardContent>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                    <Typography variant="h6" gutterBottom>
                      {price.specialType ? (
                        <>
                          {price.specialType} ({price.numberOfClasses}{" "}
                          <FormattedMessage id="payment.classes" />)
                        </>
                      ) : (
                        <>
                          {price.numberOfClasses}{" "}
                          <FormattedMessage id="payment.classes" />
                        </>
                      )}
                    </Typography>
                    {price.isAdminOnly && (
                      <Chip
                        label={<FormattedMessage id="payment.adminOnly" />}
                        color="warning"
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </Box>

                  <Typography
                    variant="h5"
                    color="primary"
                    sx={{ fontWeight: "bold", mb: 2 }}
                  >
                    {formatPrice(price.amount)}{" "}
                    <FormattedMessage id="payment.currency" />
                    {price.isAdminOnly && (
                      <Typography component="span" variant="caption" sx={{ ml: 1, color: "warning.main" }}>
                        (<FormattedMessage id="payment.free" />)
                      </Typography>
                    )}
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                    }}
                  >
                    <IconButton
                      onClick={() => decrementQuantity(price)}
                      disabled={quantity === 0}
                      size="small"
                      color="primary"
                    >
                      <Remove />
                    </IconButton>

                    <TextField
                      value={quantity}
                      onChange={(e) => {
                        const newQuantity = parseInt(e.target.value) || 0;
                        handleQuantityChange(price, newQuantity);
                      }}
                      size="small"
                      sx={{ width: 60 }}
                      inputProps={{
                        style: { textAlign: "center" },
                        min: 0,
                        type: "number",
                      }}
                    />

                    <IconButton
                      onClick={() => incrementQuantity(price)}
                      size="small"
                      color="primary"
                    >
                      <Add />
                    </IconButton>
                  </Box>

                  {isSelected && (
                    <Box sx={{ mt: 2, textAlign: "center" }}>
                      <Chip
                        label={`${formatPrice(
                          price.amount * quantity
                        )} ${intl.formatMessage({ id: "payment.currency" })}`}
                        color="primary"
                        variant="outlined"
                        size="small"
                      />
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {cartItems.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Box
            sx={{
              p: 2,
              bgcolor: "background.default",
              borderRadius: 1,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography variant="h6" gutterBottom>
              <FormattedMessage id="payment.orderSummary" />
            </Typography>

            {cartItems.map((item) => (
              <Box
                key={item.priceId}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  py: 1,
                }}
              >
                <Typography variant="body2">
                  {item.price.specialType ? (
                    <>
                      {item.price.specialType} ({item.price.numberOfClasses}{" "}
                      <FormattedMessage id="payment.classes" />)
                    </>
                  ) : (
                    <>
                      {item.price.numberOfClasses}{" "}
                      <FormattedMessage id="payment.classes" />
                    </>
                  )}{" "}
                  × {item.quantity}
                  {item.price.isAdminOnly && (
                    <Chip
                      label={<FormattedMessage id="payment.admin" />}
                      color="warning"
                      size="small"
                      sx={{ ml: 1, height: 18, fontSize: '0.7rem' }}
                    />
                  )}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                  {formatPrice(item.price.amount * item.quantity)}{" "}
                  <FormattedMessage id="payment.currency" />
                  {item.price.isAdminOnly && (
                    <Typography component="span" variant="caption" sx={{ ml: 0.5, color: "warning.main" }}>
                      (<FormattedMessage id="payment.free" />)
                    </Typography>
                  )}
                </Typography>
              </Box>
            ))}

            <Divider sx={{ my: 1 }} />

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h6">
                <FormattedMessage id="payment.total" />
              </Typography>
              <Typography
                variant="h6"
                color="primary"
                sx={{ fontWeight: "bold" }}
              >
                {formatPrice(calculateTotal())}{" "}
                <FormattedMessage id="payment.currency" />
              </Typography>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};
